import React, { useCallback, useMemo, useState } from "react";
import { Button, Grid, Link, makeStyles, Typography } from "@material-ui/core";
import styles from "../styles";
import PropTypes from "prop-types";
import { GetApp, ListAlt } from "@material-ui/icons";
import ContractDocumentComponent from "./ContractDocumentComponent";
import { useTranslation } from "react-i18next";
import { DOWNLOAD_CONTRACT_REQUIRED_DOCUMENT_TEMPLATES_QUERY } from "../queries";
import { useClientQueryWithErrorHandling } from "hooks/errorHandling";
import { convertBase64ToFile } from "utils/fileConverter";
import FileSaver from "file-saver";
import { get, sequence } from "@totalsoft/react-state-lens";
import ActionsDialog from "features/common/components/ActionsDialog";
import * as V from "@totalsoft/pure-validations";
import EmailAddressContent from "features/common/components/EmailAddressContent";
import { quoteStatus } from "constants/quoteStatus";
import cx from "classnames";
import { IconCollapseCard } from "@totalsoft/rocket-webapp-components-deprecated";

const useStyles = makeStyles(styles);
function ContractDocumentsContainer(props) {
  const { transactionId, leasingContractLens, quoteStatusId, isExternal = false } = props;
  const { alternativeEMail, setAlternativeEMail, onSendEmail, quoteId } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const clientQuery = useClientQueryWithErrorHandling();
  const [open, setOpen] = useState(false);

  const disabled = useMemo(() => {
    return quoteStatusId === quoteStatus.documentsUploaded.id;
  }, [quoteStatusId]);

  const handleOpenEmailPopUp = useCallback(() => {
    setOpen(true);
  }, []);

  const handleDownloadAll = useCallback(async () => {
    const { data } = await clientQuery(DOWNLOAD_CONTRACT_REQUIRED_DOCUMENT_TEMPLATES_QUERY, {
      variables: {
        quoteId,
        transactionId,
        contractId: leasingContractLens.documentId |> get,
        contractSiteId: leasingContractLens.siteId |> get,
        clientId: leasingContractLens.clientId |> get,
      },
      fetchPolicy: "no-cache",
    });

    if (!data) return;

    const fileURL = convertBase64ToFile(data.downloadContractRequiredDocumentTemplates.content);
    FileSaver.saveAs(fileURL, data.downloadContractRequiredDocumentTemplates.name);
  }, [clientQuery, quoteId, leasingContractLens, transactionId]);

  return (
    <IconCollapseCard
      icon={ListAlt}
      title={
        <Grid container spacing={1} justifyContent="flex-start" alignItems="baseline">
          <Grid item className={classes.fontWeightBold}>
            {leasingContractLens.documentNumber |> get}
          </Grid>
          <Grid item className={classes.reducedFont}>
            {"(" + (leasingContractLens.documentStateName |> get) + ")"}
          </Grid>
        </Grid>
      }
      content={
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {(leasingContractLens.requiredDocuments |> sequence)
              .filter((leasingContractDocumentLens) => leasingContractDocumentLens.isVisible |> get)
              .map((leasingContractDocumentLens, index) => (
                <ContractDocumentComponent
                  key={index}
                  index={index}
                  leasingContractDocumentLens={leasingContractDocumentLens}
                  clientId={leasingContractLens.clientId |> get}
                  contractId={leasingContractLens.documentId |> get}
                  contractSiteId={leasingContractLens.siteId |> get}
                  quoteId={quoteId}
                  disabled={disabled}
                />
              ))}
          </Grid>
          {!isExternal && (
            <Grid item xs={9}>
              {quoteStatusId !== quoteStatus.pendingDocumentsFromCustomer.id ? (
                <Typography variant="h6">
                  {t("SendEmailToCustomer.Click")}
                  {disabled && t("SendEmailToCustomer.Here")}

                  {!disabled && (
                    <Link onClick={handleOpenEmailPopUp} className={cx(classes.lowercase, classes.fontWeightBold)}>
                      {t("SendEmailToCustomer.Here")}
                    </Link>
                  )}
                  {t("SendEmailToCustomer.SendEmail")}
                </Typography>
              ) : (
                <Typography variant="h6">
                  {t("SendEmailToCustomer.LinkWasSentTo") + alternativeEMail}
                  {t("SendEmailToCustomer.IfNotReceived")}
                  {disabled && t("SendEmailToCustomer.Here")}
                  {!disabled && (
                    <Link
                      color="primary"
                      underline="always"
                      onClick={handleOpenEmailPopUp}
                      className={classes.lowercase}
                    >
                      {t("SendEmailToCustomer.Here")}
                    </Link>
                  )}
                  {t("SendEmailToCustomer.Resend")}
                </Typography>
              )}
            </Grid>
          )}
          <Grid item xs={isExternal ? 12 : 3}>
            <Button
              className={classes.buttonGroupRightAlign}
              variant="text"
              onClick={handleDownloadAll}
              startIcon={<GetApp />}
              disabled={false}
            >
              <span>{t("Contract.DownloadAll")} </span>
            </Button>
          </Grid>
          <ActionsDialog
            open={open}
            handleClose={useCallback(() => {
              setOpen(false);
            }, [])}
            initialState={{ email: alternativeEMail }}
            title={t("Product.ProvideUsEmailAddress")}
            validator={V.shape({ email: [V.required, V.email] |> V.stopOnFirstFailure })}
            actions={[
              {
                caption: t("General.SendEmail"),
                handle: (state, validate) => {
                  if (!validate(state)) return;
                  setAlternativeEMail(state.email);
                  onSendEmail(state.email);
                  return true;
                },
              },
            ]}
            Content={EmailAddressContent}
          />
        </Grid>
      }
    />
  );
}

ContractDocumentsContainer.propTypes = {
  transactionId: PropTypes.string.isRequired,
  leasingContractLens: PropTypes.object.isRequired,
  alternativeEMail: PropTypes.string,
  setAlternativeEMail: PropTypes.func,
  onSendEmail: PropTypes.func,
  quoteStatusId: PropTypes.number,
  isExternal: PropTypes.bool,
  quoteId: PropTypes.number,
};

export default ContractDocumentsContainer;
